import { ReactNode, useCallback, useMemo } from 'react';
import { PaginationOptions, SortDescriptor, Table, useTable } from '@backstage/ui';
import { DataColumnConfig, DataRow } from './types';

export interface DataTableProps {
  columnConfig: DataColumnConfig[];
  data: any[];
  loading?: boolean;
  error?: Error;
  emptyState?: ReactNode;
  pagination?: boolean;
  /** Overrides the default pagination (page of 5 / none) when provided. */
  paginationOptions?: PaginationOptions;
  onRowClick?: (row: DataRow) => void;
}

/** @public */
export function DataTable(props: Readonly<DataTableProps>) {
  const {
    columnConfig,
    data,
    loading,
    error,
    emptyState,
    pagination = false,
    paginationOptions,
    onRowClick,
  } = props;

  const tableData: DataRow[] = useMemo(
    () =>
      data.map(row => ({
        ...row,
        id: crypto.randomUUID(),
      })),
    [data],
  );

  const sortFn = useCallback(
    (items: DataRow[], sort: SortDescriptor) => {
      const column = columnConfig.find(c => c.id === sort.column);
      if (!column?.sortValue) {
        return items;
      }
      const getValue = column.sortValue;
      const direction = sort.direction === 'descending' ? -1 : 1;
      return [...items].sort(
        (a, b) => getValue(a).localeCompare(getValue(b)) * direction,
      );
    },
    [columnConfig],
  );

  const { tableProps } = useTable({
    mode: 'complete',
    data: tableData,
    sortFn,
    paginationOptions: paginationOptions ?? {
      type: pagination ? 'page' : 'none',
      pageSize: pagination ? 5 : undefined,
    },
  });

  return (
    <Table
      columnConfig={columnConfig}
      {...tableProps}
      loading={loading}
      error={error}
      emptyState={emptyState}
      rowConfig={onRowClick ? { onClick: onRowClick } : undefined}
    />
  );
}
